/**
 * Holds the render-loop and jump-handler calls main.ts makes against the OSM
 * minimap while its Leaflet chunk is still loading (issue #130). The minimap is
 * now constructed from a lazily-fetched chunk (Leaflet is ~150 kB and never
 * needed for the first terrain paint), so for a short window after boot the
 * real minimap does not exist yet. This gate makes that window safe:
 *
 *   1. No throw. `update()` runs every ~200 ms from the render loop and
 *      `recentre()`/`setTurbines()`/`invalidateSize()` fire from user
 *      interactions; before the minimap attaches they are absorbed rather than
 *      dereferencing an undefined map (hard constraint #7 — a still-loading
 *      minimap degrades quietly, never crashes the viewer).
 *   2. No stale-jump. The gate remembers only the LATEST camera pose and
 *      turbine set, and any pending recentre, then flushes them the instant the
 *      real minimap attaches — so a deferred minimap opens already centred on
 *      the current camera position, never on the boot origin with a later snap
 *      (the issue #81 failure class, which deferring construction would
 *      otherwise re-introduce).
 *
 * Purely an orientation aid — nothing here is ever read by raypath/ or feeds
 * any LOS result (hard constraint #1). One-time setup that only happens once
 * the chunk resolves (setViewpoints, setCompact + its subscription, the
 * bottom-sheet exclusivity wiring) is done directly on the real minimap by the
 * loader in main.ts and is intentionally not proxied here.
 */
import type { MarkerPoint } from './minimapMarkers';

/** The subset of OsmMinimap that main.ts calls from outside the loader (the
 * render loop, the jump handlers, resize, and a turbine-size swap). */
export interface DeferredMinimapTarget {
  update(x: number, z: number, bearingDeg: number): void;
  recentre(camera?: { x: number; z: number }): void;
  setTurbines(proposed: MarkerPoint[], operational: MarkerPoint[]): void;
  invalidateSize(): void;
}

export class LazyMinimap {
  private target?: DeferredMinimapTarget;
  private lastPose?: { x: number; z: number; bearingDeg: number };
  private lastTurbines?: { proposed: MarkerPoint[]; operational: MarkerPoint[] };
  private pendingRecentre = false;
  private pendingRecentreCamera?: { x: number; z: number };

  /** True once the real minimap has attached. */
  get ready(): boolean {
    return this.target !== undefined;
  }

  /** Binds the resolved minimap and replays the current state onto it (latest
   * turbines, latest pose, any pending recentre) so it appears already synced
   * to where the camera is now. */
  attach(target: DeferredMinimapTarget): void {
    this.target = target;
    if (this.lastTurbines) target.setTurbines(this.lastTurbines.proposed, this.lastTurbines.operational);
    if (this.lastPose) target.update(this.lastPose.x, this.lastPose.z, this.lastPose.bearingDeg);
    if (this.pendingRecentre) {
      target.recentre(this.pendingRecentreCamera);
      this.pendingRecentre = false;
      this.pendingRecentreCamera = undefined;
    }
  }

  update(x: number, z: number, bearingDeg: number): void {
    this.lastPose = { x, z, bearingDeg };
    this.target?.update(x, z, bearingDeg);
  }

  recentre(camera?: { x: number; z: number }): void {
    if (this.target) {
      this.target.recentre(camera);
    } else {
      this.pendingRecentre = true;
      this.pendingRecentreCamera = camera;
    }
  }

  setTurbines(proposed: MarkerPoint[], operational: MarkerPoint[]): void {
    this.lastTurbines = { proposed, operational };
    this.target?.setTurbines(proposed, operational);
  }

  invalidateSize(): void {
    this.target?.invalidateSize();
  }
}
